import dayjs from 'dayjs';
import { useState } from 'react';
import DatePicker from 'react-datepicker';
import { Control, FieldPath, useController } from 'react-hook-form';

import { useFiltersContext } from '@/context/FiltersContext/FiltersContextProvider';
import { useOnClickOutside } from '@/hooks';
import Input from '@/storybook/components/FormCollection/Input/Input';
import CalendarIcon from '@/storybook/icons/CalendarIcon';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { SingleDatePickerFooter } from './components/SingleDatePickerFooter';
import { SingleDatePickerHeader } from './components/SingleDatePickerHeader';
import { getStartOfDay, isValidDate, viewUpdaters } from './helpers';
import styles from './SingleDateFilter.module.css';
import { CalendarView } from './SingleDateFilter.types';

export type SingleDateFilterProps<
  TFieldValues extends Record<string, unknown> = Record<string, unknown>,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  control: Control<TFieldValues>;
  name: TName;
  disabled?: boolean;
  label?: string;
  labelPosition?: 'top' | 'sideLeft' | 'sideRight' | 'none';
  defaultDate?: Date | null;
};

const MIN_SELECTABLE_DATE = (() => {
  const twoYearsAgo = new Date();
  twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
  return getStartOfDay(twoYearsAgo);
})();

const MAX_SELECTABLE_DATE = getStartOfDay(new Date());

function SingleDateFilter<TFieldValues extends Record<string, unknown>, TName extends FieldPath<TFieldValues>>({
  control,
  name,
  disabled,
  label,
  labelPosition = 'top',
  defaultDate = null,
  ...props
}: SingleDateFilterProps<TFieldValues, TName>) {
  const { t } = useTranslation();
  const { field } = useController({ name, control });
  const { filterState, handleApplyFilter } = useFiltersContext();

  const dateFromfilter = isValidDate(filterState.singleDate) ? filterState.singleDate : null;
  const initialDate = dateFromfilter ?? defaultDate;

  const [shouldDisplayCustomDatePicker, setShouldDisplayCustomDatePicker] = useState(false);
  const [date, setDate] = useState<Date | null>(initialDate);
  const [view, setView] = useState<CalendarView>('week');

  const handleCloseDatePicker = (reset = true) => {
    if (reset) {
      setDate(dateFromfilter ?? defaultDate);
    }
    setShouldDisplayCustomDatePicker(false);
  };

  const datePickerWrapperRef = useOnClickOutside(() => handleCloseDatePicker(true));

  const handleChange = (selectedDate: Date | null) => {
    if (!selectedDate) return;

    const current = dayjs(date);
    const updater = viewUpdaters[view];
    const newDate = updater(current, selectedDate);

    setDate(newDate.toDate());
  };

  const accept = (selectedDate: Date | null) => {
    field.onChange(selectedDate);
    handleApplyFilter({ singleDate: selectedDate });
    handleCloseDatePicker(false);
  };

  const displayValue = date ? dayjs(date).format('DD MMM YYYY') : t('global.filters.selectDate');

  return (
    <div className={styles.filterWrapper} ref={datePickerWrapperRef}>
      <Input
        name={name}
        value={displayValue}
        iconRight={CalendarIcon}
        onClick={() => setShouldDisplayCustomDatePicker(true)}
        disabled={disabled}
        label={label}
        labelPosition={labelPosition}
        className={cx(styles.datePickerInput, shouldDisplayCustomDatePicker && styles.datePickerActiveInput)}
        readOnly
        {...props}
      />
      {shouldDisplayCustomDatePicker && (
        <div className={styles.datePickerWrapper}>
          <div className={styles.absoluteWrapper}>
            <DatePicker
              selected={date}
              onChange={handleChange}
              inline
              renderCustomHeader={customHeaderProps => (
                <SingleDatePickerHeader {...customHeaderProps} view={view} setView={setView} setDate={setDate} />
              )}
              showMonthYearPicker={view === 'month'}
              showYearPicker={view === 'year'}
              yearItemNumber={20}
              minDate={MIN_SELECTABLE_DATE}
              maxDate={MAX_SELECTABLE_DATE}
            />
            <SingleDatePickerFooter onAccept={() => accept(date)} label={t('global.filters.ok')} />
          </div>
        </div>
      )}
    </div>
  );
}

export default SingleDateFilter;
